
import React, { useState, useMemo, useEffect } from 'react'; 
import { FavoriteBorder, Star, AddShoppingCart } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  // Slider is removed
} from '@mui/material';
import axios from 'axios'; 
// ✅ NEW IMPORT: useShop hook को import करें
import { useShop } from '../context/ShopContext'; 


// 🛑 All Jewellery Banner Image URL 
const ALL_JEWELLERY_BANNER_IMAGE = 'http://rathaagoldjewelleryinca.com/images/slide/slide1.jpg';


// ---
// Product Data Type (Unified, matching the structure used in ProductList.tsx)
// ---

// Unified Product type for display
interface Product {
  id: number; // Using srno from backend as id
  itemId?: number; // Optional
  imageUrl: string; // The URL for the image
  name: string; // itemName from backend
  price: number; // price from backend
  stock: number; // stockQuantity from backend
  category: string; // category from backend
  rating: number; // Added rating for sorting/display
  style: string; // Added style for filtering
  stone_metal: string; // IMPORTANT: 'Gold' or 'Silver' for cart type determination
}

// Default filter options for the sidebar
const filterOptions = {
  category: ['Rings', 'Pendants', 'Necklaces', 'Earrings', 'Bracelets', 'Chains'],
  stone_metal: ['Gold', 'Silver', 'Rose Gold', 'Diamond', 'Kundan', 'Platinum'],
  style: ['Solitaire', 'Band', 'Pendant', 'Traditional', 'Studs', 'Jhumka', 'Chain', 'Curb'],
};


// ---
// Product Card Component (Updated for Backend Cart Integration)
// ---

// Updated ProductCard prop type to use the new Product interface
const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  // ✅ useShop hook का उपयोग करें
  const { addItemToCart } = useShop(); 

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation(); 
    
    // 1. productType निर्धारित करें ('gold' या 'silver')
    const productType = product.stone_metal.toLowerCase();
    
    if (productType !== 'gold' && productType !== 'silver') {
        alert(`त्रुटि: ${product.name} के लिए प्रोडक्ट टाइप निर्धारित नहीं किया जा सका।`);
        return;
    }

    await addItemToCart(product.id, productType, 1, product.name); 
  };
  
  const handleAddToWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert(`Added "${product.name}" to Wishlist! (Dummy Action)`);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer">
      
      <div className="relative w-full h-72 overflow-hidden">
        <div className="w-full h-full">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          />
        </div>
        
        {/* <button
          className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md text-red-500 hover:text-red-700 transition"
          aria-label="Add to Wishlist"
          onClick={handleAddToWishlist}
        >
          <FavoriteBorder fontSize="small" />
        </button> */}
      </div>
      
      <div className="p-3 text-center">
        {/* Category tag - using product.category from API */}
        <span className="text-xs font-semibold uppercase text-amber-700 bg-amber-100 px-2 py-1 rounded mb-1 inline-block">
            {product.category}
        </span>

        <p className="block text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">
          {product.name}
        </p>
        
        <p className="text-lg font-bold text-amber-900 mt-1">
          ₹{product.price.toLocaleString('en-IN')}
        </p>

        <div className="flex justify-center items-center mt-1">
          {/* Note: If the backend doesn't provide rating, you might need to mock it or hide this */}
          <Star sx={{ fontSize: 16, color: 'gold' }} />
          <span className="text-sm text-gray-600 ml-1">{(product.rating || 4.5).toFixed(1)}</span>
        </div>
        
        <button 
          className="mt-3 w-full py-2 flex items-center justify-center gap-2 bg-amber-700 text-white rounded-md hover:bg-amber-800 transition"
          onClick={handleAddToCart} // ✅ Updated Logic
        >
          <AddShoppingCart fontSize="small" />
          <span>Add To Cart</span>
        </button>
      </div>
    </div>
  );
};

// ---
// Filter Sidebar Component (Price Filter Removed)
// ---

const FilterSidebar: React.FC<{ 
  filters: typeof filterOptions,
  selectedFilters: Record<string, string[]>,
  // priceRange prop removed
  onFilterChange: (category: string, value: string) => void,
  // onPriceChange prop removed
}> = ({ filters, selectedFilters, onFilterChange }) => {

  // Helper for rendering checkboxes
  const renderCheckboxes = (category: keyof typeof filterOptions, title: string) => (
    <div className="mb-4">
      <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
      <List dense className="p-0">
        {filters[category].map((item) => (
          <ListItem key={item} disablePadding className="py-0 min-h-[30px]">
            <Checkbox
              size="small"
              checked={selectedFilters[category]?.includes(item) || false}
              onChange={() => onFilterChange(category, item)}
            />
            <ListItemText primary={item} className="text-sm" />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <div className="hidden lg:block w-64 p-4 bg-gray-50 border-r border-gray-200 sticky top-0 h-full overflow-y-auto">
      <h3 className="text-xl font-bold text-amber-900 mb-4 border-b pb-2">All Filters</h3>

      {/* Price Filter section removed */}
      
      {/* Category Filter - IMPORTANT FOR ALL JEWELLERY */}
      {renderCheckboxes('category', 'Product Category')}

      {/* Stone Type / Metal Filter */}
      {renderCheckboxes('stone_metal', 'Stone Type / Metal')}
      
      {/* Style Filter */}
      {renderCheckboxes('style', 'Style / Type')}
      
    </div>
  );
};


// ---
// Main AllJewellery Page Component (Price Logic Removed)
// ---

const AllJewelleryPage: React.FC = () => {
  const [allProducts, setAllProducts] = useState<Product[]>([]); // New state for fetched products
  const [loading, setLoading] = useState(true); // New state for loading
  const [error, setError] = useState<string | null>(null); // New state for error
  const [sortBy, setSortBy] = useState('latest');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  // Price range state and related functions removed
  

  // --- Data Fetching Logic (Copied and adapted from ProductList.tsx) ---
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        let goldProducts: Product[] = [];
        let silverProducts: Product[] = [];
  
        // Fetch Gold Products
        // const goldResponse = await axios.get<any[]>('http://localhost:8080/api/admin/gold-products');
        const goldResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/gold-products');
        goldProducts = goldResponse.data.map(goldItem => ({
          id: goldItem.srno,
          itemId: goldItem.itemId,
          // Use imagePath from the first image, or a placeholder
          imageUrl: goldItem.images && goldItem.images.length > 0
            ? goldItem.images[0].imagePath
            : 'https://dummyimage.com/300x350/cccccc/000000&text=No+Image', 
          name: goldItem.itemName,
          price: goldItem.price,
          stock: goldItem.stockQuantity,
          category: goldItem.category || 'Gold', // Default to 'Gold' if missing
          rating: 4.5, // Mock rating since it's not in the response structure
          style: 'N/A', // Mock style since it's not in the response structure
          stone_metal: 'Gold', // Default stone_metal - Used for cart type
        }));
  
        // Fetch Silver Products
        // const silverResponse = await axios.get<any[]>('http://localhost:8080/api/admin/silver-products');
        const silverResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/silver-products');
        silverProducts = silverResponse.data.map(silverItem => ({
          id: silverItem.srno,
          itemId: silverItem.itemId,
          // Use imagePath from the first image, or a placeholder
          imageUrl: silverItem.images && silverItem.images.length > 0
            ? silverItem.images[0].imagePath
            : 'https://dummyimage.com/300x350/cccccc/000000&text=No+Image',
          name: silverItem.itemName,
          price: silverItem.price,
          stock: silverItem.stockQuantity,
          category: silverItem.category || 'Silver', // Default to 'Silver' if missing
          rating: 4.2, // Mock rating
          style: 'N/A', // Mock style
          stone_metal: 'Silver', // Default stone_metal - Used for cart type
        }));
  
        // Combine and set all products
        setAllProducts([...goldProducts, ...silverProducts]);

      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load jewellery. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []); 
  // ---------------------------------------------------------------------


  // Price Change Handler removed


  // FILTERING LOGIC (Using fetched allProducts)
  const filteredProducts = useMemo(() => {
    let currentProducts = allProducts; // Use the fetched state

    // 1. Filter by Price Range (REMOVED)

    // 2. Filter by Checkbox Categories
    Object.keys(selectedFilters).forEach(category => {
      const selectedValues = selectedFilters[category];
      
      if (selectedValues && selectedValues.length > 0) {
        currentProducts = currentProducts.filter(product => {
          // Check if the product property's value is included in the selected filters
          return selectedValues.includes(product[category as keyof Product] as string);
        });
      }
    });

    return currentProducts;
  }, [allProducts, selectedFilters]); // priceRange removed from dependencies

  // SORTING LOGIC (Unchanged)
  const sortedAndFilteredProducts = useMemo(() => {
    const sorted = [...filteredProducts].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating-desc') return b.rating - a.rating;
      return a.id - b.id; // Default to latest (by ID)
    });
    return sorted;
  }, [filteredProducts, sortBy]);


  // FILTER CHANGE HANDLER (Unchanged)
  const handleFilterChange = (category: string, value: string) => {
    setSelectedFilters(prev => {
      const currentValues = prev[category] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      if (newValues.length === 0) {
        const { [category]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [category]: newValues };
    });
  };

  // --- Render Loading/Error States ---
  if (loading) {
    return <div className="text-center py-20 text-xl text-amber-800">Loading beautiful jewellery...</div>;
  }

  if (error) {
    return <div className="text-center py-20 text-xl text-red-600">{error}</div>;
  }


  return (
    <div className="flex flex-col min-h-screen bg-white">
      
      {/* 🛑 Banner Section with Background Image */}
      <div 
        className="w-full h-[400px] text-white p-4 py-6 md:py-8 lg:py-10 shadow-lg relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${ALL_JEWELLERY_BANNER_IMAGE})` }}
      >
        {/* Banner Text - Aligned to the right */}
        <div className="relative z-10 w-full md:w-1/2 ml-auto mr-4 md:mr-10 text-right pt-24"> 
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold font-[Cinzel] mb-3 leading-tight "> 
                Explore Our Complete Jewellery Collection
            </h1>
            <p className="text-sm sm:text-base md:text-lg font-light opacity-95"> 
                Rings, Necklaces, Earrings, and more, all in one place.
            </p>
        </div>


      </div>
      
      {/* Main Content Area: Sidebar + Products */}
      <div className="flex flex-1"> 
        
        {/* Sidebar - Filters */}
        <FilterSidebar 
          filters={filterOptions} 
          selectedFilters={selectedFilters}
          // priceRange prop removed
          onFilterChange={handleFilterChange}
          // onPriceChange prop removed
        />
        
        {/* Products Display */}
        <div className="flex-1 p-4 lg:p-8">
          
          {/* Header and Sort By */}
          <div className="flex justify-between items-center mb-6 border-b pb-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-800">
              All Jewellery 
            </h1>
            
            <FormControl variant="outlined" size="small" className="min-w-[120px] bg-white">
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as string)}
                label="Sort by"
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b45309 !important' }, 
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a16207 !important' }
                }} 
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="price-asc">Price: Low to High</MenuItem>
                <MenuItem value="price-desc">Price: High to Low</MenuItem>
                <MenuItem value="rating-desc">Rating</MenuItem>
              </Select>
            </FormControl>
          </div>
          
          {/* Products Grid */}
          {sortedAndFilteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {sortedAndFilteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-lg text-gray-600">
                No products found matching your filters.
            </div>
          )}
          
        </div>
        
      </div>
      
    </div>
  );
};

// Renaming the component to AllJewelleryPage for consistency
export default AllJewelleryPage;